import {useDispatch, useSelector} from 'react-redux';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {
    GetExtraDataService,
    GetVCardSettings,
    UpdateVCardSettings
} from '../../../../../reduxToolkit/Employee/EmployeeApi';
import {toast} from 'react-toastify';
import {updateComopanyImg} from '../../../../../Apis/companydata';
import {getPhotoPicker, PHOTO_PICKER_MODEL} from '../../../../../components/PhotoPicker/SubComponent/Models/Models';
import {errorMsg} from '../../../../../Apis/functions';
import { IconCubeDefault } from '../../../../../components/buttons/IconButtons';

/**
 *
 * @Author: Cecilia López Martínez
 * @JobTitle : Full Stack Developer - IBL
 * @Email : clopez@ibl.mx
 *
 */
const VCardSettingsApp = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [contractorId, setContractorId] = useState(sessionStorage.getItem('contractorId'));
    const [supplierId, setSupplierId] = useState(sessionStorage.getItem('providerId'));

    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);

    const [colorVCard, setColorVCard] = useState('#4757f8');
    const [availableHeaders, setAvailableHeaders] = useState([]);
    const [photoPicker, setPhotoPicker] = useState({...PHOTO_PICKER_MODEL, preview: IconCubeDefault})
    const [modified, setModified] = useState(0);

    const [assignedItems, setAssignedItems] = useState({
        phone_number_work: null,
        phone_number_personal: null,
        web: null,
        street: null,
        postal_code: null,
        city: null,
        state: null,
        country: null,
    });

    useEffect(() => {
        dispatch(GetExtraDataService()).then(
            ({
                 payload: {
                     data: { data, success },
                 },
             }) => {
                if (success) {
                    const headersArray = Object.keys(data)
                        .filter((key) => key.startsWith('header') && data[key])
                        .map((key) => ({
                            title: data[key],
                            isRequired: data[`${key}IsRequired`],
                        }));
                    setAvailableHeaders(headersArray);
                }
            },
        );

        dispatch(GetVCardSettings()).then(
            ({
                 payload: {
                     data: { data, success },
                 },
             }) => {
                if (success) {
                    setColorVCard(data.color);
                    if(data?.image){
                        setPhotoPicker({...PHOTO_PICKER_MODEL, preview: IconCubeDefault, previewImage: `data:;base64,${data.image}`} );
                    }
                    setAssignedItems({
                        phone_number_work: data.workPhone || null,
                        phone_number_personal: data.personalPhone || null,
                        web: data.urlAddress || null,
                        street: data.street || null,
                        postal_code: data.postalCode || null,
                        city: data.city || null,
                        state: data.state || null,
                        country: data.country || null,
                    });
                }
            },
        );
    }, [modified]);

    const handleDragStart = (title, event) => {
        event.dataTransfer.setData('text/plain', title);
    };

    const handleDropAssigned = (event, type) => {
        event.preventDefault();
        const title = event.dataTransfer.getData('text/plain');
        if (!assignedItems[type]) {
            setAssignedItems({ ...assignedItems, [type]: title });
            setAvailableHeaders(availableHeaders.filter((header) => header.title !== title));
        }
    };

    const handleDropAvailable = (event) => {
        event.preventDefault();
        const title = event.dataTransfer.getData('text/plain');
        const currentAssignedKeys = Object.keys(assignedItems);
        const itemKey = currentAssignedKeys.find((key) => assignedItems[key] === title);
        if (itemKey) {
            const newAssignedItems = { ...assignedItems, [itemKey]: null };
            setAssignedItems(newAssignedItems);
            setAvailableHeaders((prev) => {
                if (!prev.some((header) => header.title === title)) {
                    return [...prev, { title }];
                }
                return prev;
            });
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const handleUpdateVCardSettings = () => {
        const body = {
            city: assignedItems.city,
            country: assignedItems.country,
            personalPhone: assignedItems.phone_number_personal,
            postalCode: assignedItems.postal_code,
            state: assignedItems.state,
            street: assignedItems.street,
            urlAddress: assignedItems.web,
            workPhone: assignedItems.phone_number_work,
            color: colorVCard,
        };
        dispatch(UpdateVCardSettings(body)).then(
            ({
                 payload: {
                     data: { data, code, success },
                 },
             }) => {
                if (success === true) {
                    toast.success(t('has_been_updated_successfully'));
                    setModified(prev => prev + 1);
                } else {
                    errorMsg(code)
                }
            },
        );
        let formData = new FormData();
        function generateUUID() {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                var r = (Math.random() * 16) | 0,
                    v = c === 'x' ? r : (r & 0x3) | 0x8;
                return v.toString(16);
            });
        }

        let updateCompanyImg = getPhotoPicker(photoPicker);
        const validUuid = generateUUID();
        formData.append('id', validUuid);
        formData.append('option', 'vcard');
        formData.append('file', updateCompanyImg);
        if (updateCompanyImg !== undefined && updateCompanyImg != null) {
            setPhotoPicker({...PHOTO_PICKER_MODEL, preview: IconCubeDefault})
            updateComopanyImg(formData);
            setModified(prev => prev + 1);
        }
    };
    /**
     * Removes an assigned header from a specific vCard slot
     * and returns it back to the available headers list.
     */
    const handleRemoveAssigned = (key) => {
        const title = assignedItems[key];

        if (!title) return;

        setAssignedItems((prev) => ({
            ...prev,
            [key]: null,
        }));

        setAvailableHeaders((prev) => {
            if (!prev.some((header) => header.title === title)) {
                return [...prev, { title }];
            }
            return prev;
        });
    };

    
    return {
        t,
        permission,
        DEV,
        dispatch,
        navigate,
        contractorId,
        supplierId,
        availableHeaders,
        assignedItems,
        colorVCard,
        setColorVCard,
        handleDragStart,
        handleDropAssigned,
        handleDropAvailable,
        handleDragOver,
        handleUpdateVCardSettings,
        photoPicker,
        setPhotoPicker,
        handleRemoveAssigned,
    };
};

export default VCardSettingsApp;
